import React, { useEffect } from "react";

import { MdmObjectDto, SortDirection, useGetObjectsPagedLazyQuery, useGetObjectsPagedQuery } from "gql/generated/types";
import { DataTablePFSEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import MapWrapper from "../components/MapWrapper";
import ObjectAndDeviceTableWrapper from "../components/ObjectAndDeviceTableWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster } from "../utils/utils";

const Objects = () => {
  const {projectId, userSettings} = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, "Objects");

  const [tableOptions, setTableOptions] = useTableFilter("Objects", {
    sortField: "identifier",
    reqSortField: "identifier",
    sortOrder: 1,
  });
  
  const [getNextObjects, { data, loading, previousData }] = useGetObjectsPagedLazyQuery({
    onError: apolloErrorToaster,
  });
  const [getIsolatedData] = useGetObjectsPagedLazyQuery();

  useEffect(() => {
    if(userSettings) {
      getNextObjects({
        variables: {
          input: {
            projectId,
            take: tableDetails?.rows || 10,
            skip: tableOptions.first,
            sort: [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: tableOptions.andFilters,
            filter: tableOptions.filter,
          }
        },
      })
    }
  }, [userSettings, projectId, tableOptions, tableDetails, getNextObjects]);

  const objectInfoBtn = (rowData: MdmObjectDto) => (
    <Link to={`objects/${rowData.id}`} title={T("mdm.object-info")}>
      {rowData.identifier}
    </Link>
  );

  const objectNameTemplate = (rowData: MdmObjectDto) => (
    <div className="truncate-text text-right lg:text-left" title={rowData.title ?? ""}>
      {rowData.title}
    </div>
  );

  const columns: TableColumn<MdmObjectDto>[] = [
    {
      field: "identifier",
      header: T("mdm.object-code"),
      body: objectInfoBtn,
      sortable: true,
      columnKey: "identifier",
    },
    {
      field: "title",
      header: T("word.object-name"),
      body: objectNameTemplate,
      sortable: true,
      columnKey: "objectName",
    },
    {
      field: "address.fullAddress",
      header: T("word.address"),
      sortable: true,
      columnKey: "address",
    },
    {
      field: "location.latitude",
      header: T("word.latitude"),
      sortable: true,
      columnKey: "latitude",
    },
    {
      field: "location.longitude",
      header: T("word.longitude"),
      sortable: true,
      columnKey: "longitude",
    },
  ];

  const objectData = data?.mdmObjectsByProjectIdPaged.items || [];
  const previousObjectData = previousData?.mdmObjectsByProjectIdPaged.items || [];
  const tableData = (objectData.length ? objectData : previousObjectData) as MdmObjectDto[];

  const total =
    data?.mdmObjectsByProjectIdPaged.pageInfo?.total ??
    previousData?.mdmObjectsByProjectIdPaged.pageInfo?.total;

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header={T("word.table")}>
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={tableData}
          tableName="Objects"
          containerName="MdmObject"
          loading={loading}
          setTableOptions={setTableOptions}
          tableOptions={tableOptions}
          totalItems={total || 0}
          getQueryData={getNextObjects}
          getIsolatedData={getIsolatedData}
        />
      </TabPanel>
      <TabPanel header={T("word.map")}>
        <ObjectProvider tableOptions={tableOptions} records={total || 100}/>
      </TabPanel>
    </TabView>
  );
};

export default Objects;

type ObjectProviderProps = {
  tableOptions: DataTablePFSEvent;
  records: number;
};
const ObjectProvider: React.FC<ObjectProviderProps> = ({tableOptions,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetObjectsPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: tableOptions.andFilters,
        filter: tableOptions.filter,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={(data?.mdmObjectsByProjectIdPaged.items ?? []) as MdmObjectDto[]} />
    </MapWrapper>
  );
}